
export interface AskeperPerioperatifContent {
  // Tim Operasi
  tim_operasi: {
    dokter_operator_id: number | null;
    dokter_anestesi_id: number | null;
    scrub_nurse_1_id: number | null;
    scrub_nurse_2_id: number | null;
    circulating_nurse_id: number | null;
  };

  // Info Operasi
  info_operasi: {
    diagnosa_medis: string;
    jenis_operasi: string;
    jenis_anestesi: "LA" | "RA" | "GA" | null;
  };

  asesmen: {
    nyeri: {
      skala: "tidak_nyeri" | "ringan" | "sedang" | "berat" | null;
    };
    kondisi_fisik: {
      luka: boolean;
      fraktur: boolean;
      perdarahan: boolean;
    };
    vital_sign: {
      tekanan_darah_sistolik: number | null;
      tekanan_darah_diastolik: number | null;
      nadi: number | null;
      saturasi: number | null;
      respirasi: number | null;
      suhu: number | null;
    };
    riwayat_operasi_sebelumnya: boolean;
  };

  diagnosa_keperawatan: {
    ansietas: boolean;
    nyeri_akut: boolean;
    risiko_infeksi: boolean;
    risiko_cedera: boolean;
    risiko_perdarahan: boolean;
    risiko_jatuh: boolean;
    risiko_aspirasi: boolean;
    risiko_hipotermi_perioperatif: boolean;
    risiko_syok: boolean;
    termoregulasi_tidak_efektif: boolean;
  };

  tujuan: {
    tingkat_ansietas_menurun: boolean;
    tingkat_nyeri_menurun: boolean;
    tingkat_infeksi_terkontrol: boolean;
    tingkat_cedera_terkontrol: boolean;
    tingkat_jatuh_menurun: boolean;
    tingkat_aspirasi_terkontrol: boolean;
    tingkat_perdarahan_menurun: boolean;
    tingkat_syok_terkontrol: boolean;
    termoregulasi_meningkat: boolean;
  };

  intervensi: {
    reduksi_ansietas: boolean;
    manajemen_nyeri: boolean;
    pemberian_anestesi: boolean;
    pengontrolan_infeksi_intra_operatif: boolean;
    pencegahan_cedera: boolean;
    pencegahan_perdarahan: boolean;
    manajemen_hipotermi: boolean;
    pencegahan_jatuh: boolean;
    manajemen_resusitasi_cairan: boolean;
    manajemen_sedasi: boolean;
    manajemen_syok: boolean;
  };

  kategori_operasi:
    | "bersih"
    | "bersih_terkontaminasi"
    | "terkontaminasi"
    | "kotor"
    | null;

  // Implementasi Pre Operasi
  impl_pre_operasi: {
    monitor_tanda_ansietas: boolean;
    jelaskan_prosedur_operasi: boolean;
    identifikasi_nyeri: boolean;
    kolaborasi_analgetik: boolean;
    monitoring_vital_sign: boolean;
    atur_suhu_ruangan: boolean;
    sign_in: boolean;
    monitor_intake_output: boolean;
    kolaborasi_cairan_preloading: boolean;
    identifikasi_kesiapan_informasi: boolean;
    pindah_brancart_meja_op: boolean;
    pencukuran: boolean;
    atur_suhu_ruangan_18_20: boolean;
  };

  // Implementasi Intra Operasi
  impl_intra_operasi: {
    atur_posisi_anatomi: boolean;
    pasang_ground_couter: boolean;
    pemberian_anestesi: boolean;
    cuci_tangan_bedah_steril: boolean;
    teknik_aseptik_antiseptik: boolean;
    drapping: boolean;
    hitung_instrumen_kasa_jarum: boolean;
    pastikan_esu_suction: boolean;
    time_out: boolean;
    batasi_personel_max_10: boolean;
    siapkan_pa_kultur: boolean;
    monitor_intake_output_cairan: boolean;
    monitor_vital_sign: boolean;
    sign_out: boolean;
  };

  // Implementasi Post Operasi
  impl_post_operasi: {
    posisi_sesuai_instruksi: boolean;
    beri_oksigen: boolean;
    monitor_vital_sign: boolean;
    beri_analgetik: boolean;
    dampingi_belum_sadar: boolean;
    pasang_side_rail: boolean;
    antagonis_narkotik: boolean;
    monitor_kesadaran_reflek_menelan: boolean;
    monitor_recovery_score: boolean;
  };

  // Evaluasi Keperawatan
  evaluasi: {
    nyeri: "teratasi" | "belum_teratasi" | null;
    kecemasan: "terkontrol" | "belum_terkontrol" | null;
    cedera: "tidak_terjadi" | "terjadi" | null;
    infeksi_kontrol: "dilakukan" | "tidak_dilakukan" | null;
    aspirasi: "tidak_terjadi" | "terjadi" | null;
    syok: "tidak_terjadi" | "terjadi" | null;
    jatuh: "tidak_terjadi" | "terjadi" | null;
    perdarahan: "tidak_terjadi" | "terjadi" | null;
  };
}

export interface AskeperPerioperatifInterface {
  id: number;
  pasien_rm: string;
  register_id: number;
  unit_id: string | null;
  laporan_operasi_id: number | null;
  laporan_operasi?: any;
  content: AskeperPerioperatifContent;

  perawat_pre_user_id: number | null;
  perawat_pre?: any;
  pre_signed_at: string | null;

  perawat_intra_user_id: number | null;
  perawat_intra?: any;
  intra_signed_at: string | null;

  perawat_post_user_id: number | null;
  perawat_post?: any;
  post_signed_at: string | null;

  creator_user_id: number | null;
  creator?: any;
  pasien?: any;

  created_at: string;
  updated_at: string;
}

// Helper: empty content for new form
export function emptyAskeperContent(): AskeperPerioperatifContent {
  return {
    tim_operasi: {
      dokter_operator_id: null,
      dokter_anestesi_id: null,
      scrub_nurse_1_id: null,
      scrub_nurse_2_id: null,
      circulating_nurse_id: null
    },
    info_operasi: {
      diagnosa_medis: "",
      jenis_operasi: "",
      jenis_anestesi: null
    },
    asesmen: {
      nyeri: { skala: null },
      kondisi_fisik: { luka: false, fraktur: false, perdarahan: false },
      vital_sign: {
        tekanan_darah_sistolik: null,
        tekanan_darah_diastolik: null,
        nadi: null,
        saturasi: null,
        respirasi: null,
        suhu: null
      },
      riwayat_operasi_sebelumnya: false
    },
    diagnosa_keperawatan: {
      ansietas: false,
      nyeri_akut: false,
      risiko_infeksi: false,
      risiko_cedera: false,
      risiko_perdarahan: false,
      risiko_jatuh: false,
      risiko_aspirasi: false,
      risiko_hipotermi_perioperatif: false,
      risiko_syok: false,
      termoregulasi_tidak_efektif: false
    },
    tujuan: {
      tingkat_ansietas_menurun: false,
      tingkat_nyeri_menurun: false,
      tingkat_infeksi_terkontrol: false,
      tingkat_cedera_terkontrol: false,
      tingkat_jatuh_menurun: false,
      tingkat_aspirasi_terkontrol: false,
      tingkat_perdarahan_menurun: false,
      tingkat_syok_terkontrol: false,
      termoregulasi_meningkat: false
    },
    intervensi: {
      reduksi_ansietas: false,
      manajemen_nyeri: false,
      pemberian_anestesi: false,
      pengontrolan_infeksi_intra_operatif: false,
      pencegahan_cedera: false,
      pencegahan_perdarahan: false,
      manajemen_hipotermi: false,
      pencegahan_jatuh: false,
      manajemen_resusitasi_cairan: false,
      manajemen_sedasi: false,
      manajemen_syok: false
    },
    kategori_operasi: null,
    impl_pre_operasi: {
      monitor_tanda_ansietas: false,
      jelaskan_prosedur_operasi: false,
      identifikasi_nyeri: false,
      kolaborasi_analgetik: false,
      monitoring_vital_sign: false,
      atur_suhu_ruangan: false,
      sign_in: false,
      monitor_intake_output: false,
      kolaborasi_cairan_preloading: false,
      identifikasi_kesiapan_informasi: false,
      pindah_brancart_meja_op: false,
      pencukuran: false,
      atur_suhu_ruangan_18_20: false
    },
    impl_intra_operasi: {
      atur_posisi_anatomi: false,
      pasang_ground_couter: false,
      pemberian_anestesi: false,
      cuci_tangan_bedah_steril: false,
      teknik_aseptik_antiseptik: false,
      drapping: false,
      hitung_instrumen_kasa_jarum: false,
      pastikan_esu_suction: false,
      time_out: false,
      batasi_personel_max_10: false,
      siapkan_pa_kultur: false,
      monitor_intake_output_cairan: false,
      monitor_vital_sign: false,
      sign_out: false
    },
    impl_post_operasi: {
      posisi_sesuai_instruksi: false,
      beri_oksigen: false,
      monitor_vital_sign: false,
      beri_analgetik: false,
      dampingi_belum_sadar: false,
      pasang_side_rail: false,
      antagonis_narkotik: false,
      monitor_kesadaran_reflek_menelan: false,
      monitor_recovery_score: false
    },
    evaluasi: {
      nyeri: null,
      kecemasan: null,
      cedera: null,
      infeksi_kontrol: null,
      aspirasi: null,
      syok: null,
      jatuh: null,
      perdarahan: null
    }
  };
}
